import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';

import { CreateJournalEntrySchema, fmt, fmtDate } from '@dcc-erp/shared';
import type { CreateJournalEntry, CreateJournalLine, JournalEntry } from '@dcc-erp/shared';
import { Icon } from '@dcc-erp/ui';

import { Can } from '../components/Can';
import { HelpButton } from '../components/help/HelpButton';
import { CreateJournalModal } from '../components/journal/CreateJournalModal';
import { JournalDetailCard } from '../components/journal/JournalDetailCard';
import {
  JV_IMPORT_COLS,
  JV_IMPORT_FIELDS,
  JV_PREVIEW_COLS,
  JV_TEMPLATE_EXAMPLES,
  periodFromDate,
} from '../components/journal/jvImport';
import { JE_STATUS, JV_TABS, tabStatuses, type JvTabKey } from '../components/journal/status';
import { PageHead } from '../components/layout';
import {
  SpreadsheetImportModal,
  type ImportResult,
  type ParsedUnit,
} from '../components/SpreadsheetImportModal';
import { useAuth } from '../lib/auth';
import { usePermissions } from '../lib/auth/usePermissions';
import {
  useActiveCompany,
  useActiveCompanyId,
  useBranches,
  useCoa,
  useCreateJournalEntry,
  useDepartments,
  useJournalEntries,
  useJournalEntry,
  type JournalEntryWithApprovals,
} from '../lib/queries';
import { exportRows, headerIndexer, todayStamp, type ExportColumn } from '../lib/xlsx';

const SEARCH_BG =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%238a8a93' stroke-width='2'%3E%3Ccircle cx='11' cy='11' r='7'/%3E%3Cpath d='M21 21l-4.3-4.3'/%3E%3C/svg%3E\")";

export const Route = createFileRoute('/journal')({
  validateSearch: (s: Record<string, unknown>): { jv?: string } => {
    const result: { jv?: string } = {};
    if (typeof s.jv === 'string') result.jv = s.jv;
    return result;
  },
  component: JournalPage,
});

function JournalPage() {
  const companyId = useActiveCompanyId();
  const company = useActiveCompany();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { roles } = usePermissions();

  const { jv } = Route.useSearch();

  const [tab, setTab] = useState<JvTabKey>('ALL');
  const [q, setQ] = useState('');
  const [dateFrom, setDateFrom] = useState(''); // '' = all dates (default)
  const [dateTo, setDateTo] = useState('');
  // Deep-link: ?jv=<entryId> navigates from GL Schedule "ดู JV" — seed selectedId from the
  // search param on mount so the detail card opens for that entry automatically.
  const [selectedId, setSelectedId] = useState<string | undefined>(jv);
  const [showCreate, setShowCreate] = useState(false);
  const [editEntry, setEditEntry] = useState<JournalEntryWithApprovals | undefined>();
  const [cloneEntry, setCloneEntry] = useState<JournalEntryWithApprovals | undefined>();
  const [showImport, setShowImport] = useState(false);

  const filters = useMemo(
    () => ({ ...(dateFrom ? { dateFrom } : {}), ...(dateTo ? { dateTo } : {}) }),
    [dateFrom, dateTo],
  );
  const { data: journals = [], isPending, error } = useJournalEntries(companyId, filters);

  // code → nameTh resolver for the detail card's line table.
  const { data: coa = [] } = useCoa(companyId);
  const accountName = useMemo(() => {
    const m = new Map(coa.map((c) => [c.code, c.nameTh]));
    return (code: string) => m.get(code) ?? '';
  }, [coa]);

  // Server submittedBy stores `preferredUsername || email || entraOid` — match it
  // so the detail card's SoD gate compares apples to apples.
  const viewerIdentity = user?.preferredUsername ?? user?.email ?? user?.entraOid ?? '';

  // Masters for the simple-voucher importer: resolve branchCode/departmentCode →
  // id. Account codes pass through untouched (the server validates them).
  const { data: branches = [] } = useBranches(companyId);
  const { data: departments = [] } = useDepartments(companyId);
  const createJournal = useCreateJournalEntry(companyId);

  const branchByCode = useMemo(
    () => new Map(branches.map((b) => [b.code.toLowerCase(), b.id])),
    [branches],
  );
  const departmentByCode = useMemo(
    () => new Map(departments.map((d) => [d.code.toLowerCase(), d.id])),
    [departments],
  );

  // Tab counts over the loaded (date-filtered) set — one pass, client-side.
  const counts = useMemo(() => {
    const c: Record<JvTabKey, number> = {
      ALL: journals.length,
      DRAFT: 0,
      PENDING: 0,
      APPROVED: 0,
      POSTED: 0,
      REJECTED: 0,
    };
    for (const j of journals) {
      for (const t of JV_TABS) {
        if (t.statuses.includes(j.status)) c[t.key]++;
      }
    }
    return c;
  }, [journals]);

  // Visible rows = tab status filter, then client-side text search across
  // voucherNo / description / createdBy.
  const visible = useMemo(() => {
    const set = tabStatuses(tab);
    let rows = set.length ? journals.filter((j) => set.includes(j.status)) : journals;
    if (q) {
      const needle = q.toLowerCase();
      rows = rows.filter(
        (j) =>
          j.voucherNo.toLowerCase().includes(needle) ||
          j.description.toLowerCase().includes(needle) ||
          (j.createdBy ?? '').toLowerCase().includes(needle),
      );
    }
    return rows;
  }, [journals, tab, q]);

  // Detail card pulls the SELECTED JV's lines+approvals — single by-id query.
  const { data: detail } = useJournalEntry(companyId, selectedId);

  // Header register export. The list endpoint returns headers + computed totals
  // (no per-line breakdown), so this is a register, not an import round-trip.
  const EXPORT_COLS: ExportColumn<JournalEntry>[] = [
    { header: 'voucherNo', value: (r) => r.voucherNo, width: 18 },
    { header: 'voucherDate', value: (r) => String(r.voucherDate).slice(0, 10), width: 12 },
    { header: 'period', value: (r) => r.period, width: 10 },
    { header: 'description', value: (r) => r.description, width: 40 },
    { header: 'status', value: (r) => r.status, width: 12 },
    { header: 'createdBy', value: (r) => r.createdBy ?? '', width: 16 },
    { header: 'postedBy', value: (r) => r.postedBy ?? '', width: 16 },
  ];

  async function handleExport() {
    const code = company?.code ?? 'COMPANY';
    await exportRows(
      `บันทึกบัญชี_${code}_${todayStamp()}.xlsx`,
      'บันทึกบัญชี',
      EXPORT_COLS,
      journals,
    );
  }

  // Parse a grid into simple-voucher create payloads — one row = one voucher
  // with a single debit + single credit line of equal amount.
  function parse(grid: string[][]): {
    units: ParsedUnit<CreateJournalEntry>[];
    headerError: string | null;
  } {
    const header = grid[0];
    if (!header) return { units: [], headerError: 'ไฟล์ว่างเปล่า' };
    const { idx, cell } = headerIndexer(header);
    for (const req of [
      'voucherDate',
      'description',
      'debitAccountCode',
      'creditAccountCode',
      'amount',
    ]) {
      if (idx(req) === -1) return { units: [], headerError: `ไม่พบคอลัมน์ที่จำเป็น: ${req}` };
    }
    const units: ParsedUnit<CreateJournalEntry>[] = [];
    for (let r = 1; r < grid.length; r++) {
      const cols = grid[r];
      if (!cols || cols.every((c) => c.trim() === '')) continue;
      const rowNo = r + 1;

      const branchCode = cell(cols, 'branchCode');
      const branchId = branchCode ? branchByCode.get(branchCode.toLowerCase()) : null;
      if (branchCode && !branchId) {
        units.push({ rowNo, data: null, error: `ไม่พบสาขา: ${branchCode}` });
        continue;
      }
      const departmentCode = cell(cols, 'departmentCode');
      const departmentId = departmentCode
        ? departmentByCode.get(departmentCode.toLowerCase())
        : null;
      if (departmentCode && !departmentId) {
        units.push({ rowNo, data: null, error: `ไม่พบหน่วยงาน: ${departmentCode}` });
        continue;
      }

      const voucherDate = cell(cols, 'voucherDate');
      const period = cell(cols, 'period') || periodFromDate(voucherDate);
      const amount = Number(cell(cols, 'amount'));
      const debitAccountCode = cell(cols, 'debitAccountCode');
      const creditAccountCode = cell(cols, 'creditAccountCode');
      const lines: CreateJournalLine[] = [
        {
          accountCode: debitAccountCode,
          debit: amount,
          credit: 0,
          description: null,
          refKey: null,
          branchId: branchId ?? null,
          departmentId: departmentId ?? null,
        },
        {
          accountCode: creditAccountCode,
          debit: 0,
          credit: amount,
          description: null,
          refKey: null,
          branchId: branchId ?? null,
          departmentId: departmentId ?? null,
        },
      ];
      const candidate = {
        voucherDate,
        period,
        description: cell(cols, 'description'),
        prefix: cell(cols, 'prefix') || 'JV',
        branchId: branchId ?? null,
        departmentId: departmentId ?? null,
        lines,
      };
      const parsed = CreateJournalEntrySchema.safeParse(candidate);
      units.push(
        parsed.success
          ? { rowNo, data: parsed.data, error: null }
          : {
              rowNo,
              data: null,
              error: parsed.error.issues[0]?.message ?? 'ข้อมูลไม่ถูกต้อง',
            },
      );
    }
    return { units, headerError: null };
  }

  async function runImport(
    valid: ParsedUnit<CreateJournalEntry>[],
    onProgress: (done: number, total: number) => void,
  ): Promise<ImportResult> {
    const res: ImportResult = { created: 0, updated: 0, failed: 0, errors: [] };
    for (let i = 0; i < valid.length; i++) {
      const u = valid[i];
      if (!u?.data) continue;
      try {
        await createJournal.mutateAsync({ data: u.data, idempotencyKey: crypto.randomUUID() });
        res.created++;
      } catch (e) {
        res.failed++;
        res.errors.push({
          code: `แถว ${u.rowNo}`,
          message: e instanceof Error ? e.message : 'เกิดข้อผิดพลาด',
        });
      }
      onProgress(i + 1, valid.length);
    }
    return res;
  }

  return (
    <div className="page">
      <PageHead
        crumbs={['บัญชีแยกประเภท', 'บันทึกบัญชี']}
        title="บันทึกบัญชี (Journal Entries)"
        actions={
          <>
            <HelpButton guide="journal" />
            <Can permission="journal.create">
              <button className="btn" onClick={() => setShowImport(true)} disabled={!companyId}>
                <Icon name="upload" size={14} /> นำเข้า
              </button>
            </Can>
            <button
              className="btn"
              onClick={() => void handleExport()}
              disabled={journals.length === 0}
            >
              <Icon name="download" size={14} /> ส่งออก
            </button>
            <Can permission="journal.create">
              <button className="btn primary" onClick={() => setShowCreate(true)}>
                <Icon name="plus" size={14} /> สร้างใบสำคัญ
              </button>
            </Can>
          </>
        }
      />

      <div className="page-tabs">
        {JV_TABS.map((t) => (
          <div
            key={t.key}
            className={`pt ${tab === t.key ? 'active' : ''}`}
            onClick={() => setTab(t.key)}
          >
            {`${t.label} (${counts[t.key]})`}
          </div>
        ))}
      </div>

      <div className="tbl-shell">
        <div className="filter-row">
          <input
            className="input search w-lg"
            placeholder="ค้นหา เลขที่ใบสำคัญ / รายละเอียด / ผู้บันทึก"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            style={{
              paddingLeft: 28,
              backgroundImage: SEARCH_BG,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: '8px center',
            }}
          />
          <input
            className="input w-sm"
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
          />
          <span className="faint">ถึง</span>
          <input
            className="input w-sm"
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
          />
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
            <button
              className="btn sm"
              onClick={() => {
                setQ('');
                setDateFrom('');
                setDateTo('');
              }}
            >
              รีเซ็ต
            </button>
          </div>
        </div>
        <div className="tbl-wrap" style={{ maxHeight: 540 }}>
          <table className="tbl">
            <thead>
              <tr>
                <th style={{ width: 170 }}>เลขที่ใบสำคัญ</th>
                <th style={{ width: 100 }}>วันที่</th>
                <th>รายการ</th>
                <th style={{ width: 90 }}>งวด</th>
                <th className="num" style={{ width: 130 }}>
                  เดบิต
                </th>
                <th className="num" style={{ width: 130 }}>
                  เครดิต
                </th>
                <th style={{ width: 140 }}>สถานะ</th>
                <th style={{ width: 130 }}>ผู้บันทึก</th>
              </tr>
            </thead>
            <tbody>
              {isPending ? (
                <tr>
                  <td colSpan={8} className="muted" style={{ padding: 24, textAlign: 'center' }}>
                    กำลังโหลด…
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td
                    colSpan={8}
                    className="muted"
                    style={{ padding: 24, textAlign: 'center', color: 'var(--negative)' }}
                  >
                    โหลดล้มเหลว: {error.message}
                  </td>
                </tr>
              ) : visible.length === 0 ? (
                <tr>
                  <td colSpan={8} className="muted" style={{ padding: 24, textAlign: 'center' }}>
                    ยังไม่มีรายการ — กดปุ่ม &quot;สร้างใบสำคัญ&quot; ทางขวาบน
                  </td>
                </tr>
              ) : (
                visible.map((j) => (
                  <tr
                    key={j.id}
                    onClick={() => setSelectedId(j.id)}
                    style={{
                      cursor: 'pointer',
                      background: j.id === selectedId ? 'var(--row-selected, #f0f6ff)' : undefined,
                    }}
                  >
                    <td className="code">
                      <span style={{ color: 'var(--brand)', fontWeight: 500 }}>{j.voucherNo}</span>
                    </td>
                    <td>{fmtDate(j.voucherDate)}</td>
                    <td>{j.description}</td>
                    <td className="code muted">{j.period}</td>
                    <td className="num tnum">{fmt(Number(j.totalDebit ?? 0), 2)}</td>
                    <td className="num tnum">{fmt(Number(j.totalCredit ?? 0), 2)}</td>
                    <td>
                      <span className={`badge ${JE_STATUS[j.status].tone}`}>
                        <span className="bdot" />
                        {j.status === 'PENDING'
                          ? `รออนุมัติ · ขั้น ${j.currentStep ?? 1}`
                          : JE_STATUS[j.status].label}
                      </span>
                    </td>
                    <td className="muted">{j.createdBy ?? '—'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="tbl-footer">
          <span>แสดง {visible.length} รายการ</span>
        </div>
      </div>

      {detail && companyId && (
        <div className="card" style={{ marginTop: 'var(--section-gap)' }}>
          <div className="card-body">
            <JournalDetailCard
              entry={detail}
              companyId={companyId}
              viewerIdentity={viewerIdentity}
              viewerRoles={roles}
              accountName={accountName}
              onEdit={() => setEditEntry(detail)}
              onViewGl={() => void navigate({ to: '/ledger', search: { period: detail.period } })}
              onDeleted={() => setSelectedId(undefined)}
              onClone={() => setCloneEntry(detail)}
            />
          </div>
        </div>
      )}

      {showCreate && (
        <CreateJournalModal companyId={companyId} onClose={() => setShowCreate(false)} />
      )}

      {cloneEntry && (
        <CreateJournalModal
          companyId={companyId}
          cloneFrom={cloneEntry}
          onClose={() => setCloneEntry(undefined)}
        />
      )}

      {editEntry && (
        <CreateJournalModal
          companyId={companyId}
          mode="edit"
          entry={editEntry}
          onClose={() => setEditEntry(undefined)}
        />
      )}

      {showImport && companyId && (
        <SpreadsheetImportModal<CreateJournalEntry>
          title="นำเข้าใบสำคัญอย่างง่าย"
          intro={
            <>
              หนึ่งแถว = หนึ่งใบสำคัญอย่างง่าย ที่มี
              <b>บัญชีเดบิตหนึ่งบัญชีและบัญชีเครดิตหนึ่งบัญชี</b>
              ในจำนวนเงินเท่ากัน — สำหรับใบสำคัญหลายบรรทัด ให้ใช้ปุ่ม “สร้างใบสำคัญ”
            </>
          }
          fields={JV_IMPORT_FIELDS}
          templateColumns={[...JV_IMPORT_COLS]}
          templateExamples={JV_TEMPLATE_EXAMPLES}
          templateFilename="journal_simple_template.xlsx"
          sheetName="บันทึกบัญชี"
          parse={parse}
          previewColumns={JV_PREVIEW_COLS}
          runImport={runImport}
          onClose={() => setShowImport(false)}
        />
      )}
    </div>
  );
}
